import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCsrfToken } from '../../Hooks/useCsrfToken';
import RosterProfileCard from '../../Roster/RosterProfileCard/RosterProfileCard';
import LeagueRostersCard from '../../League/LeagueRostersCard/LeagueRostersCard';
import LeaguePortraitCard from '../../League/LeaguePortraitCard/LeaguePortraitCard';
import RosterScheduleCard from '../../Roster/RosterScheduleCard/RosterScheduleCard';
import LeagueStandingsCard from '../../Roster/LeagueStandingsCard/LeagueStandingsCard';
import VerticalIconBar from '../../Navigation/VerticalIconBar/VerticalIconBar';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

const handleRostersClick = () => {
	window.location.href = `/rosters`;
};

export default function RosterProfilePage({
	get_roster_by_user_endpoint,
	get_player_endpoint,
	get_players_endpoint,
	get_team_endpoint,
	update_roster_by_user_endpoint,
	create_roster_player_by_roster_endpoint,
	delete_roster_player_by_roster_endpoint,
	get_roster_player_note_endpoint,
	create_roster_player_note_endpoint,
	update_roster_player_note_endpoint,
	get_notes_by_roster_endpoint,
	update_notes_by_roster_endpoint,
	get_draft_notes_by_roster_endpoint,
	update_draft_notes_by_roster_endpoint,
	get_lessons_learned_notes_by_roster_endpoint,
	update_lessons_learned_notes_by_roster_endpoint,
	get_prospects_endpoint,
	scrape_and_update_ffpc_roster_endpoint,
	get_roster_ai_insights_endpoint,
	get_roster_ai_trade_ideas_endpoint,
	get_player_ai_insights_endpoint,
	get_team_ai_insights_endpoint,
	get_league_rosters_endpoint,
	get_roster_schedule_info_endpoint,
	get_roster_schedule_data_by_user_endpoint,
	scrape_and_update_ffpc_league_rosters_endpoint,
	scrape_and_update_ffpc_schedule_endpoint,
	fetch_and_update_sleeper_league_rosters_endpoint,
	fetch_and_update_sleeper_schedule_endpoint,
	fetch_and_update_yahoo_league_rosters_endpoint,
	fetch_and_update_yahoo_schedule_endpoint,
	get_league_standings_endpoint
}) {
	const { user } = useAuth();
	const csrfToken = useCsrfToken();
	const [
		activeView,
		setActiveView
	] = useState('roster');

	const [
		rosterLoaded,
		setRosterLoaded
	] = useState(false);

	const [
		leagueRostersLoaded,
		setLeagueRostersLoaded
	] = useState(false);

	const [
		currentViewLoaded,
		setCurrentViewLoaded
	] = useState(false);

	const [
		leagueRostersUpdated,
		setLeagueRostersUpdated
	] = useState(0);

	const [
		isScrapingLeagueRosters,
		setIsScrapingLeagueRosters
	] = useState(false);
	const [
		currentRosterTeamName,
		setCurrentRosterTeamName
	] = useState(null);
	const [
		scheduleUpdated,
		setScheduleUpdated
	] = useState(0);
	const [
		isScrapingSchedule,
		setIsScrapingSchedule
	] = useState(false);
	const [
		rosterData,
		setRosterData
	] = useState(null);
	const [
		isLoadingRosterData,
		setIsLoadingRosterData
	] = useState(false);

	// Reset loading state when switching views
	useEffect(
		() => {
			setCurrentViewLoaded(false);

			// Reset specific view loading states when switching away from them
			if (activeView === 'roster') {
				setRosterLoaded(false);
			} else if (activeView === 'league-rosters') {
				setLeagueRostersLoaded(false);
			} else if (activeView === 'schedule') {
				// Schedule view loads immediately, mark as loaded
				setCurrentViewLoaded(true);
			}
		},
		[
			activeView
		]
	);

	// Show VerticalIconBar based on current view loading state
	const showIconBar = currentViewLoaded || activeView === 'schedule';

	const handleIconClick = (view) => {
		setActiveView(view);

		// If switching to league-rosters view, trigger league roster scraping for FFPC rosters
		if (view === 'league-rosters') {
			fetchRosterData();
			triggerLeagueRosterScraping();
		}

		// If switching to schedule view, trigger schedule scraping for FFPC rosters
		if (view === 'schedule') {
			fetchRosterData();
			triggerScheduleScraping();
		}
	};

	const fetchRosterData = async () => {
		if (isLoadingRosterData || rosterData) {
			return; // Don't fetch if already loading or data is already loaded
		}

		try {
			setIsLoadingRosterData(true);
			const rosterId = window.location.pathname.split('/').pop();
			const userId = user && user.id;

			if (!userId) {
				console.error('No user ID available');
				return;
			}

			const rosterUrl = get_roster_by_user_endpoint
				.replace('user_id', userId)
				.replace('roster_id', rosterId);

			const response = await fetch(rosterUrl, {
				credentials: 'same-origin',
				cache: 'no-cache',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken,
					'Cache-Control': 'no-cache',
					Pragma: 'no-cache'
				}
			});

			if (response.ok) {
				const data = await response.json();
				setRosterData(data.data);
			} else {
				console.error('Failed to fetch roster data');
			}
		} catch (error) {
			console.error('Error fetching roster data:', error);
		} finally {
			setIsLoadingRosterData(false);
		}
	};

	const triggerLeagueRosterScraping = async () => {
		// Prevent multiple simultaneous scraping operations
		if (isScrapingLeagueRosters) {
			return;
		}

		try {
			setIsScrapingLeagueRosters(true);
			const rosterId = window.location.pathname.split('/').pop();
			// Get the current user ID from the auth context
			const userId = user && user.id;
			if (!userId) {
				console.error('No user ID available');
				return;
			}
			const rosterUrl = get_roster_by_user_endpoint
				.replace('user_id', userId)
				.replace('roster_id', rosterId);
			const response = await fetch(rosterUrl, {
				credentials: 'same-origin',
				cache: 'no-cache',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken,
					'Cache-Control': 'no-cache',
					Pragma: 'no-cache'
				}
			});

			const responseText = await response.text();

			let data;
			try {
				data = JSON.parse(responseText);

				// Store the current roster's team name for sorting
				if (data.data && data.data.team_name) {
					setCurrentRosterTeamName(data.data.team_name);
				}
			} catch (parseError) {
				console.error('Failed to parse roster response as JSON:', parseError);
				throw parseError;
			}

			// Check if it's an FFPC roster with a roster_url
			if (
				data.data &&
				data.data.league &&
				data.data.league.platform === 'FFPC' &&
				data.data.roster_url
			) {
				const scrapeUrl = scrape_and_update_ffpc_league_rosters_endpoint.replace(
					'roster_id',
					rosterId
				);

				const scrapeResponse = await fetch(scrapeUrl, {
					method: 'POST',
					credentials: 'same-origin',
					cache: 'no-cache',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken,
						'Cache-Control': 'no-cache',
						Pragma: 'no-cache'
					},
					body: JSON.stringify({
						roster_url: data.data.roster_url,
						roster_id: rosterId
					})
				});

				const responseText = await scrapeResponse.text();

				let scrapeData;
				try {
					scrapeData = JSON.parse(responseText);
				} catch (parseError) {
					console.error('Failed to parse response as JSON:', parseError);
					throw parseError;
				}

				if (scrapeData.success) {
					setLeagueRostersUpdated((prev) => prev + 1);
					// Update roster data from the sync response
					if (scrapeData.roster) {
						setRosterData(scrapeData.roster);
					}
				} else {
					console.error('Failed to update league rosters:', scrapeData.message);
				}
			} else if (
				data.data &&
				data.data.league &&
				data.data.league.platform === 'Sleeper' &&
				data.data.roster_url
			) {
				// Check if it's a Sleeper roster with a roster_url
				const fetchUrl = fetch_and_update_sleeper_league_rosters_endpoint.replace(
					'roster_id',
					rosterId
				);

				// Extract league_id from roster_url
				const rosterUrl = data.data.roster_url;
				const leagueId = (rosterUrl.match(/leagues\/(\d+)\/team/) || [])[1];

				const fetchResponse = await fetch(fetchUrl, {
					method: 'POST',
					credentials: 'same-origin',
					cache: 'no-cache',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken,
						'Cache-Control': 'no-cache',
						Pragma: 'no-cache'
					},
					body: JSON.stringify({
						roster_id: rosterId,
						league_id: leagueId,
						username: data.data.username
					})
				});

				const responseText = await fetchResponse.text();

				let fetchData;
				try {
					fetchData = JSON.parse(responseText);
				} catch (parseError) {
					console.error('Failed to parse response as JSON:', parseError);
					throw parseError;
				}

				if (fetchData.success) {
					setLeagueRostersUpdated((prev) => prev + 1);
					// Update roster data from the sync response
					if (fetchData.roster) {
						setRosterData(fetchData.roster);
					}
				} else {
					console.error('Failed to update league rosters:', fetchData.message);
				}
			} else if (
				data.data &&
				data.data.league &&
				data.data.league.platform === 'Yahoo' &&
				data.data.roster_url
			) {
				// Check if it's a Yahoo roster with a roster_url
				const fetchUrl = fetch_and_update_yahoo_league_rosters_endpoint.replace(
					'roster_id',
					rosterId
				);

				// Extract league_id and team_id from roster_url
				const rosterUrl = data.data.roster_url;
				const yahooMatch = rosterUrl.match(/\/f1\/(\d+)\/(\d+)/);

				if (yahooMatch) {
					const leagueId = yahooMatch[1];
					const teamId = yahooMatch[2];

					// Format league and team IDs using the same pattern as existing Yahoo implementation
					const formattedLeagueId = `nfl.l.${leagueId}`;
					const formattedTeamId = `nfl.l.${leagueId}.t.${teamId}`;

					const fetchResponse = await fetch(fetchUrl, {
						method: 'POST',
						credentials: 'same-origin',
						cache: 'no-cache',
						headers: {
							'Content-Type': 'application/json',
							'X-CSRF-Token': csrfToken,
							'Cache-Control': 'no-cache',
							Pragma: 'no-cache'
						},
						body: JSON.stringify({
							roster_id: rosterId,
							league_id: formattedLeagueId,
							team_id: formattedTeamId,
							roster_url: rosterUrl
						})
					});

					const responseText = await fetchResponse.text();

					let fetchData;
					try {
						fetchData = JSON.parse(responseText);
					} catch (parseError) {
						console.error('Failed to parse response as JSON:', parseError);
						throw parseError;
					}

					if (fetchData.success) {
						setLeagueRostersUpdated((prev) => prev + 1);
						// Update roster data from the sync response
						if (fetchData.roster) {
							setRosterData(fetchData.roster);
						}
					} else {
						console.error(
							'Failed to update league rosters:',
							fetchData.message
						);
					}
				} else {
					console.error('Failed to parse Yahoo roster URL:', rosterUrl);
				}
			}
		} catch (error) {
			console.error('Error updating league rosters:', error);
		} finally {
			setIsScrapingLeagueRosters(false);
		}
	};

	const triggerScheduleScraping = async () => {
		// Prevent multiple simultaneous scraping operations
		if (isScrapingSchedule) {
			return;
		}

		try {
			setIsScrapingSchedule(true);
			const rosterId = window.location.pathname.split('/').pop();

			// Use lightweight endpoint to get only the data needed for schedule scraping
			const scheduleInfoUrl = get_roster_schedule_info_endpoint.replace(
				'roster_id',
				rosterId
			);

			const response = await fetch(scheduleInfoUrl, {
				credentials: 'same-origin',
				cache: 'no-cache',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-Token': csrfToken,
					'Cache-Control': 'no-cache',
					Pragma: 'no-cache'
				}
			});

			const responseText = await response.text();
			let data;

			try {
				data = JSON.parse(responseText);
			} catch (parseError) {
				console.error(
					'Failed to parse schedule info response as JSON:',
					parseError
				);
				throw parseError;
			}

			// Check if it's an FFPC roster with a roster_url
			if (
				data.data &&
				data.data.league &&
				data.data.league.platform === 'FFPC' &&
				data.data.roster_url
			) {
				const scrapeUrl = scrape_and_update_ffpc_schedule_endpoint.replace(
					'roster_id',
					rosterId
				);

				const scrapeResponse = await fetch(scrapeUrl, {
					method: 'POST',
					credentials: 'same-origin',
					cache: 'no-cache',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken,
						'Cache-Control': 'no-cache',
						Pragma: 'no-cache'
					},
					body: JSON.stringify({
						roster_url: data.data.roster_url,
						roster_id: rosterId
					})
				});

				const responseText = await scrapeResponse.text();
				let scrapeData;

				try {
					scrapeData = JSON.parse(responseText);
				} catch (parseError) {
					console.error('Failed to parse response as JSON:', parseError);
					throw parseError;
				}

				if (scrapeData.success) {
					setScheduleUpdated((prev) => prev + 1);
				} else {
					console.error('Failed to update schedule:', scrapeData.message);
				}
			} else if (
				// Check if it's a Sleeper roster with a roster_url
				data.data &&
				data.data.league &&
				data.data.league.platform === 'Sleeper' &&
				data.data.roster_url
			) {
				// Check if it's a Sleeper roster with a roster_url
				const fetchUrl = fetch_and_update_sleeper_schedule_endpoint.replace(
					'roster_id',
					rosterId
				);

				// Extract league_id from roster_url
				const rosterUrl = data.data.roster_url;
				const leagueId = (rosterUrl.match(/leagues\/(\d+)\/team/) || [])[1];

				if (!leagueId) {
					console.error(
						'Could not extract league_id from roster_url:',
						rosterUrl
					);
					return;
				}

				// Get username from roster data
				const username = data.data.username;

				const fetchResponse = await fetch(fetchUrl, {
					method: 'POST',
					credentials: 'same-origin',
					cache: 'no-cache',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken,
						'Cache-Control': 'no-cache',
						Pragma: 'no-cache'
					},
					body: JSON.stringify({
						roster_id: rosterId,
						league_id: leagueId,
						username: username
					})
				});

				const responseText = await fetchResponse.text();
				let fetchData;

				try {
					fetchData = JSON.parse(responseText);
				} catch (parseError) {
					console.error('Failed to parse response as JSON:', parseError);
					throw parseError;
				}

				if (fetchData.success) {
					setScheduleUpdated((prev) => prev + 1);
				} else {
					console.error('Failed to update schedule:', fetchData.message);
				}
			} else if (
				// Check if it's a Yahoo roster with a roster_url
				data.data &&
				data.data.league &&
				data.data.league.platform === 'Yahoo' &&
				data.data.roster_url
			) {
				const fetchUrl = fetch_and_update_yahoo_schedule_endpoint.replace(
					'roster_id',
					rosterId
				);

				// Extract league_id and team_id from roster_url
				const rosterUrl = data.data.roster_url;
				const yahooMatch = rosterUrl.match(/\/f1\/(\d+)\/(\d+)/);

				if (!yahooMatch) {
					console.error(
						'Could not extract league_id and team_id from roster_url:',
						rosterUrl
					);
					return;
				}

				const leagueId = yahooMatch[1];
				const teamId = yahooMatch[2];

				// Format league and team IDs using the same pattern as existing Yahoo implementation
				const formattedLeagueId = `nfl.l.${leagueId}`;
				const formattedTeamId = `nfl.l.${leagueId}.t.${teamId}`;

				const fetchResponse = await fetch(fetchUrl, {
					method: 'POST',
					credentials: 'same-origin',
					cache: 'no-cache',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken,
						'Cache-Control': 'no-cache',
						Pragma: 'no-cache'
					},
					body: JSON.stringify({
						roster_id: rosterId,
						league_id: formattedLeagueId,
						team_id: formattedTeamId,
						roster_url: rosterUrl
					})
				});

				const responseText = await fetchResponse.text();
				let fetchData;

				try {
					fetchData = JSON.parse(responseText);
				} catch (parseError) {
					console.error('Failed to parse response as JSON:', parseError);
					throw parseError;
				}

				if (fetchData.success) {
					setScheduleUpdated((prev) => prev + 1);
				} else {
					console.error('Failed to update schedule:', fetchData.message);
				}
			}
		} catch (error) {
			console.error('Error updating schedule:', error);
		} finally {
			setIsScrapingSchedule(false);
		}
	};

	// Create a ref to track when roster content is rendered
	const rosterRef = useRef(null);
	const leagueRostersRef = useRef(null);

	// Track when content is actually rendered and visible
	useEffect(
		() => {
			const checkContentLoaded = () => {
				if (activeView === 'roster' && rosterRef.current) {
					// Look for specific roster content elements that indicate data is loaded
					const hasRosterData =
						rosterRef.current.querySelector('[class*="roster"]') ||
						rosterRef.current.querySelector('[class*="player"]') ||
						rosterRef.current.textContent.trim().length > 100;
					if (hasRosterData && !currentViewLoaded) {
						setCurrentViewLoaded(true);
						setRosterLoaded(true);
					}
				}

				if (activeView === 'league-rosters' && leagueRostersRef.current) {
					const hasLeagueData =
						leagueRostersRef.current.querySelector('[class*="league"]') ||
						leagueRostersRef.current.querySelector('[class*="roster"]') ||
						leagueRostersRef.current.textContent.trim().length > 100;
					if (hasLeagueData && !currentViewLoaded) {
						setCurrentViewLoaded(true);
						setLeagueRostersLoaded(true);
					}
				}
			};

			// Check immediately, then set up an interval to check periodically
			checkContentLoaded();
			const interval = setInterval(checkContentLoaded, 200);

			// Clean up after 10 seconds to avoid infinite checking
			const timeout = setTimeout(() => {
				clearInterval(interval);
				// Fallback - show the icon bar after 10 seconds regardless
				setCurrentViewLoaded(true);
				if (activeView === 'roster') setRosterLoaded(true);
				if (activeView === 'league-rosters') setLeagueRostersLoaded(true);
			}, 10000);

			return () => {
				clearInterval(interval);
				clearTimeout(timeout);
			};
		},
		[
			activeView,
			currentViewLoaded
		]
	);

	const renderContent = () => {
		switch (activeView) {
			case 'roster':
				return (
					<div ref={rosterRef}>
						<RosterProfileCard
							get_roster_by_user_endpoint={get_roster_by_user_endpoint}
							get_player_endpoint={get_player_endpoint}
							get_players_endpoint={get_players_endpoint}
							get_team_endpoint={get_team_endpoint}
							update_roster_by_user_endpoint={update_roster_by_user_endpoint}
							create_roster_player_by_roster_endpoint={
								create_roster_player_by_roster_endpoint
							}
							delete_roster_player_by_roster_endpoint={
								delete_roster_player_by_roster_endpoint
							}
							get_roster_player_note_endpoint={get_roster_player_note_endpoint}
							create_roster_player_note_endpoint={
								create_roster_player_note_endpoint
							}
							update_roster_player_note_endpoint={
								update_roster_player_note_endpoint
							}
							get_notes_by_roster_endpoint={get_notes_by_roster_endpoint}
							update_notes_by_roster_endpoint={update_notes_by_roster_endpoint}
							get_draft_notes_by_roster_endpoint={
								get_draft_notes_by_roster_endpoint
							}
							update_draft_notes_by_roster_endpoint={
								update_draft_notes_by_roster_endpoint
							}
							get_lessons_learned_notes_by_roster_endpoint={
								get_lessons_learned_notes_by_roster_endpoint
							}
							update_lessons_learned_notes_by_roster_endpoint={
								update_lessons_learned_notes_by_roster_endpoint
							}
							get_prospects_endpoint={get_prospects_endpoint}
							scrape_and_update_ffpc_roster_endpoint={
								scrape_and_update_ffpc_roster_endpoint
							}
							get_roster_ai_insights_endpoint={get_roster_ai_insights_endpoint}
							get_player_ai_insights_endpoint={get_player_ai_insights_endpoint}
							get_team_ai_insights_endpoint={get_team_ai_insights_endpoint}
						/>
					</div>
				);
			case 'league-rosters':
				return (
					<div ref={leagueRostersRef}>
						<LeagueStandingsCard
							rosterId={window.location.pathname.split('/').pop()}
							get_league_standings_endpoint={get_league_standings_endpoint}
						/>
						<div className="mt-14">
						<div className="text-3xl font-bold mb-3 text-gray-800 dark:text-dark-text">
							<h4>League Rosters</h4>
						</div>
							{isScrapingLeagueRosters ? (
								<div className="border border-gray-300 dark:border-dark-border rounded-xl shadow-lg bg-white dark:bg-dark-card p-6">
									<div className="text-center">
										<LoadingSpinner />
									</div>
								</div>
							) : (
								<LeagueRostersCard
									rosterId={window.location.pathname.split('/').pop()}
									getLeagueRostersEndpoint={get_league_rosters_endpoint}
									leagueRostersUpdated={leagueRostersUpdated}
									getRosterAiTradeIdeasEndpoint={
										get_roster_ai_trade_ideas_endpoint
									}
									isScraping={isScrapingLeagueRosters}
									currentRosterTeamName={currentRosterTeamName}
								/>
							)}
						</div>
					</div>
				);
			case 'schedule':
				return (
					<div>
						<RosterScheduleCard
							rosterId={window.location.pathname.split('/').pop()}
							getRosterScheduleDataEndpoint={
								get_roster_schedule_data_by_user_endpoint
							}
							scheduleUpdated={scheduleUpdated}
							isScraping={isScrapingSchedule}
						/>
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20">
			<div className="relative">
				{/* League Portrait Card - shown for league-rosters and schedule views */}
				{(activeView === 'league-rosters' || activeView === 'schedule') &&
				rosterData &&
				rosterData.league && <LeaguePortraitCard league={rosterData.league} />}

				{/* Main Content */}
				{renderContent()}

				{/* Vertical Icon Bar */}
				<VerticalIconBar
					onIconClick={handleIconClick}
					activeView={activeView}
					isVisible={showIconBar}
				/>
			</div>
		</div>
	);
}
